import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { useGridApiRef } from "@mui/x-data-grid";
import { NoRowOverlay } from "../../../../table/NoRowOverlay";
import LocalizedDataGrid from "../../../../ui/datagrid/LocalizedDataGrid";
import CustomFilter from "../../../../table/CustomFilter";
import CSVDownloader from "../../../../ui/CSVDownloader";
import { getToday } from "../../../../ui/utils/DateUtils";
import { Modal } from "../../../../../shared/ui/modal/Modal";
import { store } from "../../../../../store";
import { getAnalysesByStudy } from "../../../../../redux/actions/analysis";
import { ScreenAnalysisForm } from "./AnalysisForm";
import {
  compareAnalysesByColorAndName,
  normalizeAnalysisColor,
} from "./AnalysisColorPalette";

const SPACING = 50;

const createEmptyComboFilters = () => ({
  name: "",
  reference: "",
  description: "",
  visits: "",
  container: "",
  temperature: "",
  // samples: "",
});

const toOption = (value) => ({
  value,
  label: value,
});

const uniqueSortedOptions = (values) =>
  [...new Set(values.filter(Boolean).map((value) => String(value)))]
    .sort((a, b) => a.localeCompare(b))
    .map(toOption);

const getTemperatureName = (analysis) => {
  const temperature =
    analysis?.temperatureDefinition ||
    analysis?.Temperature ||
    analysis?.temperatureName ||
    analysis?.temperature;

  if (!temperature) {
    return "";
  }

  if (typeof temperature === "string") {
    return temperature;
  }

  return temperature.name || "";
};

const getVisitNames = (analysis) =>
  [...new Set((analysis?.visits || analysis?.Visits || [])
    .map((visit) => visit?.name)
    .filter(Boolean))].sort((left, right) => left.localeCompare(right));

const getSampleTypeNames = (analysis) =>
  [
    ...new Set(
      (analysis?.sampleTypes || analysis?.SampleTypes || [])
        .map((sampleType) => sampleType?.name)
        .filter(Boolean),
    ),
  ].sort((left, right) => left.localeCompare(right));

const getAnalysisContainerName = (analysis) => {
  const container =
    analysis?.analysisContainer ||
    analysis?.container ||
    analysis?.containerName;

  if (!container) {
    return "";
  }

  if (typeof container === "string") {
    return container;
  }

  return container.name || "";
};

// const getSampleName = (sample) =>
//   sample?.patientKey || sample?.patient?.number || sample?.id || "";

export const ScreenAnalyses = ({
  tableHeight = "80vh",
  hideReferenceDescription = false,
  showHeaderAddButton = false,
  sectionTitle,
} = {}) => {
  const apiRef = useGridApiRef();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user.user);
  const adminUser = user.role === "admin";
  const studyId = location.state?.studyId;
  const studyCode = location.state?.studyCode;
  const centerNumber = location.state?.centerNumber;
  const centerId = location.state?.centerId;
  const analyses = useSelector((state) => state.analysis.analyses);
  const isLoading = useSelector((state) => state.analysis.isLoadingAnalyses);
  const [comboFilters, setComboFilters] = React.useState(
    createEmptyComboFilters,
  );
  const [selectedRowIds, setSelectedRowIds] = React.useState([]);
  const [analysisModalId, setAnalysisModalId] = React.useState(null);

  const routeState = React.useMemo(
    () => ({
      studyId,
      studyCode,
      centerNumber,
      centerId,
    }),
    [centerId, centerNumber, studyCode, studyId],
  );

  const openAnalysisModal = React.useCallback((nextAnalysisId) => {
    setAnalysisModalId(nextAnalysisId);
  }, []);

  const closeAnalysisModal = React.useCallback(() => {
    setAnalysisModalId(null);
  }, []);

  const handleAnalysisSaved = React.useCallback(async () => {
    setAnalysisModalId(null);

    if (studyId) {
      store.dispatch(getAnalysesByStudy(studyId));
    }
  }, [studyId]);

  React.useEffect(() => {
    if (!studyId) {
      navigate("/samples", { replace: true, state: routeState });
      return;
    }

    store.dispatch(getAnalysesByStudy(studyId));
  }, [navigate, routeState, studyId]);

  const rows = React.useMemo(
    () =>
      (analyses || [])
        .map((analysis) => {
          const visitNames = getVisitNames(analysis);
          const sampleTypeNames = getSampleTypeNames(analysis);
          // const sampleNames = (analysis.samples || [])
          //   .map(getSampleName)
          //   .filter(Boolean)
          //   .map((sampleName) => String(sampleName));

          return {
            ...analysis,
            nameFilter: analysis.name || "",
            referenceFilter: analysis.reference || "",
            descriptionFilter: analysis.description || "",
            visitsLabel: visitNames.join(", "),
            visitNames,
            sampleTypesLabel: sampleTypeNames.join(", "),
            containerLabel: getAnalysisContainerName(analysis),
            temperatureLabel: getTemperatureName(analysis),
            // samplesLabel: sampleNames.join(", "),
            // sampleNames,
          };
        })
        .sort(compareAnalysesByColorAndName),
    [analyses],
  );

  const nameOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.nameFilter)),
    [rows],
  );

  const descriptionOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.descriptionFilter)),
    [rows],
  );

  const referenceOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.referenceFilter)),
    [rows],
  );

  const temperatureOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.temperatureLabel)),
    [rows],
  );

  const visitOptions = React.useMemo(
    () => uniqueSortedOptions(rows.flatMap((row) => row.visitNames)),
    [rows],
  );

  const containerOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.containerLabel)),
    [rows],
  );

  // const sampleOptions = React.useMemo(
  //   () => uniqueSortedOptions(rows.flatMap((row) => row.sampleNames)),
  //   [rows],
  // );

  const updateComboFilter = React.useCallback((field, value) => {
    setComboFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value || "",
    }));
  }, []);

  const activeFilterLabels = React.useMemo(
    () =>
      [
        comboFilters.name,
        comboFilters.reference,
        comboFilters.description,
        comboFilters.visits,
        comboFilters.container,
        comboFilters.temperature,
        // comboFilters.samples,
      ].filter(Boolean),
    [comboFilters],
  );

  const handleResetFilters = React.useCallback(() => {
    setComboFilters(createEmptyComboFilters());
    apiRef.current?.setFilterModel?.({ items: [] });
  }, [apiRef]);

  const displayedAnalyses = React.useMemo(
    () =>
      rows.filter((analysis) => {
        if (comboFilters.name && analysis.nameFilter !== comboFilters.name) {
          return false;
        }

        if (
          comboFilters.reference &&
          analysis.referenceFilter !== comboFilters.reference
        ) {
          return false;
        }

        if (
          comboFilters.description &&
          analysis.descriptionFilter !== comboFilters.description
        ) {
          return false;
        }

        if (
          comboFilters.visits &&
          !analysis.visitNames.includes(comboFilters.visits)
        ) {
          return false;
        }

        if (
          comboFilters.container &&
          analysis.containerLabel !== comboFilters.container
        ) {
          return false;
        }

        if (
          comboFilters.temperature &&
          analysis.temperatureLabel !== comboFilters.temperature
        ) {
          return false;
        }

        // if (
        //   comboFilters.samples &&
        //   !analysis.sampleNames.includes(comboFilters.samples)
        // ) {
        //   return false;
        // }

        return true;
      }),
    [comboFilters, rows],
  );

  const downloadAnalyses = React.useMemo(
    () =>
      displayedAnalyses
        .filter((analysis) => selectedRowIds.includes(analysis.id))
        .map(
          ({
            nameFilter,
            descriptionFilter,
            visitsLabel,
            visitNames,
            sampleTypesLabel,
            containerLabel,
            temperatureLabel,
            // samplesLabel,
            // sampleNames,
            ...analysis
          }) => analysis,
        ),
    [displayedAnalyses, selectedRowIds],
  );

  React.useEffect(() => {
    const visibleIds = new Set(
      displayedAnalyses.map((analysis) => analysis.id),
    );
    setSelectedRowIds((currentIds) =>
      currentIds.filter((rowId) => visibleIds.has(rowId)),
    );
  }, [displayedAnalyses]);

  const columns = React.useMemo(() => {
    const comboFilterOperator = (field, label, options, matchesRow) => [
      {
        label,
        value: field,
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) return null;
          return ({ row }) => matchesRow(row, filterItem.value);
        },
        InputComponent: (props) => (
          <CustomFilter
            label={label}
            options={options}
            {...props}
            item={{
              ...props.item,
              value: props.item.value || comboFilters[field],
            }}
            applyValue={(nextItem) => {
              props.applyValue(nextItem);
              updateComboFilter(field, nextItem.value);
            }}
          />
        ),
      },
    ];

    const baseColumns = [
      {
        field: "actions",
        headerName: "",
        sortable: false,
        filterable: false,
        width: 70,
        renderCell: (params) =>
          adminUser ? (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                openAnalysisModal(params.row.id);
              }}>
              <EditIcon fontSize="small" />
            </IconButton>
          ) : null,
      },
      {
        field: "name",
        headerName: t("name"),
        flex: 4,
        minWidth: 240,
        filterable: true,
        filterOperators: comboFilterOperator(
          "name",
          t("name"),
          nameOptions,
          (row, value) => row.nameFilter === value,
        ),
        renderCell: (params) => (
          <Box
            component="span"
            sx={{
              color: normalizeAnalysisColor(params.row.color, "text.primary"),
              textShadow: "0 0 1px rgba(0, 0, 0, 0.6)",
              fontWeight: params.row.color ? 600 : 400,
            }}>
            {params.value}
          </Box>
        ),
      },
      {
        field: "visitsLabel",
        headerName: t("visits", "Visits"),
        flex: 2,
        minWidth: 140,
        filterable: true,
        filterOperators: comboFilterOperator(
          "visits",
          t("visits", "Visits"),
          visitOptions,
          (row, value) => row.visitNames.includes(value),
        ),
        renderCell: (params) => params.value || "-",
      },
      {
        field: "sampleTypesLabel",
        headerName: t("primaryBackupAbbreviation", "PRIM / BU"),
        flex: 2,
        minWidth: 140,
        sortable: true,
        filterable: false,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "containerLabel",
        headerName: t("analysisContainer", "Container"),
        flex: 1,
        minWidth: 100,
        filterable: true,
        filterOperators: comboFilterOperator(
          "container",
          t("analysisContainer", "Container"),
          containerOptions,
          (row, value) => row.containerLabel === value,
        ),
        renderCell: (params) => params.value || "-",
      },
      {
        field: "temperatureLabel",
        headerName: t("temperature", "Temperature"),
        flex: 1,
        minWidth: 100,
        filterable: true,
        filterOperators: comboFilterOperator(
          "temperature",
          t("temperature", "Temperature"),
          temperatureOptions,
          (row, value) => row.temperatureLabel === value,
        ),
      },
      // {
      //   field: "samplesLabel",
      //   headerName: t("samples"),
      //   flex: 0.2,
      //   minWidth: 160,
      //   filterable: true,
      //   filterOperators: comboFilterOperator(
      //     "samples",
      //     t("samples"),
      //     sampleOptions,
      //     (row, value) => row.sampleNames.includes(value),
      //   ),
      //   renderCell: (params) => params.value || "-",
      // },
    ];

    if (!hideReferenceDescription) {
      baseColumns.splice(
        2,
        0,
        {
          field: "reference",
          headerName: t("reference", "Reference"),
          flex: 0.2,
          minWidth: 160,
          filterable: true,
          filterOperators: comboFilterOperator(
            "reference",
            t("reference", "Reference"),
            referenceOptions,
            (row, value) => row.referenceFilter === value,
          ),
          renderCell: (params) => params.value || "-",
        },
        {
          field: "description",
          headerName: t("description"),
          flex: 0.35,
          minWidth: 220,
          filterable: true,
          filterOperators: comboFilterOperator(
            "description",
            t("description"),
            descriptionOptions,
            (row, value) => row.descriptionFilter === value,
          ),
        },
      );
    }

    return baseColumns;
  }, [
    adminUser,
    comboFilters,
    descriptionOptions,
    hideReferenceDescription,
    nameOptions,
    openAnalysisModal,
    referenceOptions,
    containerOptions,
    // sampleOptions,
    t,
    temperatureOptions,
    updateComboFilter,
    visitOptions,
  ]);

  return (
    <Grid item xs={12}>
      {showHeaderAddButton && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}>
          <Typography variant="h6">
            {sectionTitle || t("analysisList")}
          </Typography>
          {adminUser && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAnalysisModal("new")}>
              {t("add", "Add")}
            </Button>
          )}
        </Box>
      )}
      <Box
        sx={{ position: "relative", pb: downloadAnalyses.length > 0 ? 12 : 0 }}>
        <Paper sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ width: "100%", height: tableHeight, position: "relative" }}>
            <LocalizedDataGrid
              apiRef={apiRef}
              autoWidth
              slots={{
                noRowsOverlay: NoRowOverlay,
                loadingOverlay: LinearProgress,
              }}
              rows={displayedAnalyses}
              columns={columns}
              loading={isLoading}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={(newSelection) => {
                setSelectedRowIds(newSelection);
              }}
              onRowDoubleClick={(params) => {
                if (!adminUser) return;
                openAnalysisModal(params.row.id);
              }}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 50 },
                },
              }}
              pageSizeOptions={[20, 50, 100]}
              rowHeight={40}
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />
            {activeFilterLabels.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  left: 16,
                  bottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  maxWidth: "55%",
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  zIndex: 2,
                }}>
                <IconButton
                  aria-label="Reset filters"
                  onClick={handleResetFilters}
                  size="small"
                  sx={{ p: 0.25 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                  Filters: {activeFilterLabels.join(", ")}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
        {downloadAnalyses.length > 0 && (
          <Box sx={{ position: "absolute", left: 0, bottom: 16, zIndex: 3 }}>
            <CSVDownloader
              data={downloadAnalyses}
              filename={`${studyCode}${centerNumber ? `_${centerNumber}` : ""}_analyses_${getToday()}`}
              name={t("analysisList")}
            />
          </Box>
        )}
      </Box>
      {adminUser && !showHeaderAddButton && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => openAnalysisModal("new")}
          style={{ position: "absolute", right: SPACING, bottom: SPACING }}>
          <AddIcon />
        </Fab>
      )}
      <Modal
        open={analysisModalId !== null}
        onClose={closeAnalysisModal}
        title={
          analysisModalId && analysisModalId !== "new"
            ? t("editAnalysis", "Edit analysis")
            : t("createAnalysis")
        }
        maxWidth="md"
        contentSx={{ pt: 1 }}>
        {analysisModalId !== null && (
          <ScreenAnalysisForm
            analysisId={analysisModalId}
            studyContext={routeState}
            embedded
            onClose={closeAnalysisModal}
            onSaved={handleAnalysisSaved}
          />
        )}
      </Modal>
    </Grid>
  );
};
